import React, { useState } from "react";
import * as SubframeCore from "@subframe/core";
import { Accordion } from "subframe/components/Accordion";
import { Switch } from "subframe/components/Switch";
import { Alert } from "subframe/components/Alert";
import { Survey, SurveyStatus, SurveyType, AlertVariant } from "../../../types";

interface SurveyDetailsProps {
  survey: Survey;
  isLoading?: boolean;
}

const SurveyDetailsSkeleton: React.FC = () => (
  <div className="flex w-full flex-col items-start justify-end gap-1 rounded-lg border border-solid border-neutral-border bg-default-background">
    <Accordion
      trigger={
        <div className="flex w-full items-center justify-between rounded-lg px-4 py-2">
          <div className="h-5 w-32 bg-neutral-200 rounded animate-pulse" />
          <Accordion.Chevron />
        </div>
      }
      defaultOpen={true}
    >
      <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-2 border-t border-solid border-neutral-border bg-neutral-50 px-4 py-4">
        <div className="flex w-full flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="flex flex-col items-start gap-2">
            <div className="h-6 w-48 bg-neutral-200 rounded animate-pulse" />
          </div>
        </div>
        <div className="flex w-full flex-col items-start gap-1 rounded-md bg-white px-2 py-2 shadow-sm">
          {[1, 2, 3].map((item) => (
            <div key={item} className="flex w-full flex-col items-start border-b border-solid border-neutral-border px-1 py-1 last:border-b-0">
              <div className="w-full flex flex-col gap-2 justify-between sm:flex-row sm:items-start">
                <div className="w-full flex-1 h-4 bg-neutral-200 rounded animate-pulse" />
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="h-6 w-20 bg-neutral-200 rounded animate-pulse" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Accordion>
  </div>
);

export const SurveyDetails: React.FC<SurveyDetailsProps> = ({ survey, isLoading = false }) => {
  const [aiSummaryEnabled, setAiSummaryEnabled] = useState(false);

  if (isLoading) {
    return <SurveyDetailsSkeleton />;
  }

  const getStatusColor = (status: SurveyStatus | null) => {
    if (!status) {
      return {
        border: 'border-neutral-100',
        bg: 'bg-neutral-50',
        text: 'text-neutral-600',
        iconColor: 'text-neutral-600'
      };
    }

    switch (status) {
      case SurveyStatus.LOW:
        return {
          border: 'border-error-100',
          bg: 'bg-error-50',
          text: 'text-[#991b1bff]',
          iconColor: 'text-error-700'
        };
      case SurveyStatus.MEDIUM:
        return {
          border: 'border-warning-100',
          bg: 'bg-warning-50',
          text: 'text-[#854d0eff]',
          iconColor: 'text-warning-800'
        };
      case SurveyStatus.HIGH:
        return {
          border: 'border-success-100',
          bg: 'bg-success-50',
          text: 'text-[#166534ff]',
          iconColor: 'text-success-800'
        };
    }
  };

  const getStatusText = (status: SurveyStatus | null, score: number) => {
    if (!status) {
      return `${score}`;
    }

    switch (status) {
      case SurveyStatus.LOW:
        return `${score} Low`;
      case SurveyStatus.MEDIUM:
        return `${score} Medium`;
      case SurveyStatus.HIGH:
        return `${score} High`;
    }
  };

  return (
    <div className="flex w-full flex-col items-start justify-end gap-1 rounded-lg border border-solid border-neutral-border bg-default-background">
      <Accordion
        trigger={
          <div className="flex w-full items-center justify-between rounded-lg px-4 py-2">
            <span className="grow shrink-0 basis-0 text-body font-body text-default-font">
              Survey Details
            </span>
            <Accordion.Chevron />
          </div>
        }
        defaultOpen={true}
      >
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-2 border-t border-solid border-neutral-border bg-neutral-50 px-4 py-4">
          <div className="flex w-full flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="flex flex-col items-start gap-2">
              {survey.keyConcerns && survey.keyConcerns.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-body font-body text-default-font">
                    AI summerize
                  </span>
                  <Switch
                    checked={aiSummaryEnabled}
                    onCheckedChange={setAiSummaryEnabled}
                  />
                </div>
              )}
              {aiSummaryEnabled && survey.keyConcerns && survey.keyConcerns.length > 0 && (
                <Alert
                  className="h-auto w-full flex-none md:w-auto"
                  variant={AlertVariant.BRAND}
                  icon="FeatherSparkles"
                  title="Key concerns"
                  description={survey.keyConcerns.join(', ')}
                />
              )}
            </div>
          </div>
          <div className="flex w-full flex-col items-start gap-1 rounded-md bg-white px-2 py-2 shadow-sm">
            {survey.questions && survey.questions.length > 0 ? (
              survey.questions.map((question) => {
                const statusColors = getStatusColor(question.status);
                return (
                  <div key={question.id} className="flex w-full flex-col items-start border-b border-solid border-neutral-border px-1 py-1 last:border-b-0">
                    <div className="w-full flex flex-col gap-2">
                      <div className="w-full flex flex-col gap-2 justify-between sm:flex-row sm:items-start">
                        <span className="w-full flex-1 text-body font-body text-default-font break-words">
                          {question.question}
                        </span>
                        {question.type !== SurveyType.FREE_RESPONSE && (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <div className={`flex h-6 items-center gap-1 rounded-md border border-solid ${statusColors.border} ${statusColors.bg} px-2`}>
                              <SubframeCore.Icon
                                className={`text-label font-label ${statusColors.iconColor}`}
                                name={null}
                              />
                              <span className={`whitespace-nowrap text-label font-label ${statusColors.text}`}>
                                {getStatusText(question.status, question.score)}
                              </span>
                              <SubframeCore.Icon
                                className={`text-label font-label ${statusColors.iconColor}`}
                                name={null}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      {question.type === SurveyType.FREE_RESPONSE && (
                        <div className="w-full">
                          <span className="w-full text-body font-body text-subtext-color">
                            &quot;{question.answer}&quot;
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex w-full items-center justify-center py-4">
                <span className="text-body font-body text-subtext-color">No survey questions available</span>
              </div>
            )}
            {survey.openEndedResponse && (
              <div className="flex w-full flex-col items-start gap-2 px-1 py-1">
                <span className="w-full text-body font-body text-default-font">
                  Anything else you want to share?
                </span>
                <span className="w-full text-body font-body text-subtext-color">
                  &quot;{survey.openEndedResponse}&quot;
                </span>
              </div>
            )}
          </div>
        </div>
      </Accordion>
    </div>
  );
}; 